import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { enviroment } from "../enviroment";
import { useProfileContext } from "./ProfileContext";
import { Role } from "../core/models/entities/Role";
import {
  SALES_MARKUP_OFF,
  SalesMarkup,
} from "../utils/functions/salesMarkup";

interface PriceVisibilityContextProps {
  hidePrices: boolean;
  showCustomerPrices: boolean;
  togglePriceVisibility: () => void;
  toggleCustomerPrices: () => void;
  // "Sales price markup" (flag priceMarkupEnabled): the dealer's display
  // markup, read through useSalesMarkup(). The key exists on the value
  // object ONLY with the flag on (flag off = today's four members, byte for
  // byte). enabled=true only for a Vendor/Admin (never an EndUser, never
  // in workshop mode) whose profile org switched the markup on.
  salesMarkup?: SalesMarkup;
}

const PriceVisibilityContext = createContext<
  PriceVisibilityContextProps | undefined
>(undefined);

interface PriceVisibilityProviderProps {
  children: ReactNode;
}

export function PriceVisibilityProvider({
  children,
}: PriceVisibilityProviderProps) {
  const [hidePrices, setHidePrices] = useState<boolean>(false);
  const [showCustomerPrices, setShowCustomerPrices] = useState<boolean>(false);
  const { role, organization, workshopMode, profile } = useProfileContext();

  const togglePriceVisibility = () => {
    setHidePrices((prev) => !prev);
  };

  const toggleCustomerPrices = () => {
    if (role === Role.EndUser || workshopMode) return;
    setShowCustomerPrices((prev) => !prev);
  };

  const customerHasPricesHidden =
    role === Role.EndUser && organization?.hidePrices === true;

  // OR semantics: the per-user workshop flag always wins - the session
  // "Hide Prices" toggle can never undo it.
  const effectiveHidePrices =
    hidePrices || customerHasPricesHidden || workshopMode;

  // Sales price markup (flag priceMarkupEnabled): on only for a dealer
  // (Vendor, or an admin impersonating a vendor org - the role is derived
  // from the SHOWN org's profile) whose org switched it on; never for an
  // EndUser (stripped server-side anyway) and never in workshop mode. The
  // profile's displayMarkup is absent while the API flag is off = off.
  const profileMarkup = profile?.userOrganizationInformations?.displayMarkup;
  const salesMarkupActive =
    enviroment.priceMarkupEnabled &&
    (role === Role.Vendor || role === Role.Admin) &&
    !workshopMode &&
    profileMarkup?.enabled === true &&
    Number.isFinite(profileMarkup.percent);
  const salesMarkupPercent = salesMarkupActive ? profileMarkup!.percent : 0;
  const salesMarkup = useMemo<SalesMarkup>(
    () =>
      salesMarkupActive
        ? { enabled: true, percent: salesMarkupPercent }
        : SALES_MARKUP_OFF,
    [salesMarkupActive, salesMarkupPercent],
  );

  // Flagged users are treated like end users: if a price ever leaks through
  // a missing hide-gate, it is the customer-facing price, never vendor cost.
  // Markup wins over the session "Show Customer Prices" (the sales price IS
  // the customer-facing number); always false with the flag off.
  const effectiveShowCustomerPrices =
    role === Role.EndUser || workshopMode
      ? true
      : salesMarkupActive
        ? false
        : showCustomerPrices;

  // Admin perf FE-2 (flag adminPerfNavMemoEnabled): stable toggles + one
  // value object per (hidePrices, showCustomerPrices) pair. Flag off = the
  // literal object per render as before (hooks are called either way).
  const stableTogglePriceVisibility = useCallback(() => {
    setHidePrices((prev) => !prev);
  }, []);
  const stableToggleCustomerPrices = useCallback(() => {
    if (role === Role.EndUser || workshopMode) return;
    setShowCustomerPrices((prev) => !prev);
  }, [role, workshopMode]);
  const memoValue = useMemo(
    () => ({
      hidePrices: effectiveHidePrices,
      showCustomerPrices: effectiveShowCustomerPrices,
      togglePriceVisibility: stableTogglePriceVisibility,
      toggleCustomerPrices: stableToggleCustomerPrices,
      ...(enviroment.priceMarkupEnabled ? { salesMarkup } : {}),
    }),
    [
      effectiveHidePrices,
      effectiveShowCustomerPrices,
      stableTogglePriceVisibility,
      stableToggleCustomerPrices,
      salesMarkup,
    ],
  );

  return (
    <PriceVisibilityContext.Provider
      value={
        enviroment.adminPerfNavMemoEnabled
          ? memoValue
          : {
              hidePrices: effectiveHidePrices,
              showCustomerPrices: effectiveShowCustomerPrices,
              togglePriceVisibility,
              toggleCustomerPrices,
              ...(enviroment.priceMarkupEnabled ? { salesMarkup } : {}),
            }
      }
    >
      {children}
    </PriceVisibilityContext.Provider>
  );
}

export function usePriceVisibility() {
  const context = useContext(PriceVisibilityContext);
  if (context === undefined) {
    throw new Error(
      "usePriceVisibility must be used within a PriceVisibilityProvider"
    );
  }
  return context;
}

// Exported for useSalesMarkup (reads the context tolerantly: no provider or
// a test double without this export = markup off).
export { PriceVisibilityContext };
